import type {
  TransactionResponse,
  TransactionReceipt,
} from '@ethersproject/providers';
import { errors } from 'ethers';
import { ethErrorCodeMap } from '@lib/utils';
import { useToast } from '@chakra-ui/react';
import { IntlShape } from 'react-intl';

export async function handleTxResponse(
  intl: IntlShape,
  txResponse: TransactionResponse,
  toast: ReturnType<typeof useToast>,
  isReplacedTx = false,
): Promise<TransactionReceipt | void> {
  if (!isReplacedTx) {
    toast({
      title: intl.formatMessage({ defaultMessage: '交易确认中，请勿离开页面' }),
      description: intl.formatMessage({ defaultMessage: '预计需要 15-60 秒' }),
      status: 'info',
      duration: 20000,
    });
  }
  try {
    const receipt = await txResponse.wait(1);
    toast({
      title: intl.formatMessage({ defaultMessage: '交易回执已收到' }),
      description: intl.formatMessage({
        defaultMessage: '正在确认购买请求，请稍候...',
      }),
    });
    return receipt;
  } catch (err: any) {
    if (err.code === errors.CALL_EXCEPTION) {
      const receipt: TransactionReceipt = err.receipt;
      if (receipt.status === 0) {
        toast({
          title: intl.formatMessage({ defaultMessage: '链上交易状态异常' }),
          description: 'status=' + receipt.status,
          status: 'error',
        });
      } else {
        toast({
          title: intl.formatMessage({ defaultMessage: '交易确认出现异常' }),
          description: err.reason || err.message,
          status: 'error',
        });
      }
    } else if (err.code === errors.TRANSACTION_REPLACED) {
      const receipt: TransactionReceipt = err.receipt;
      const replacement: TransactionResponse = err.replacement;
      enum Reason {
        replaced = 'replaced',
        cancelled = 'cancelled',
        repriced = 'repriced',
      }
      const reasonTextMap: Record<Reason, string> = {
        [Reason.replaced]: intl.formatMessage({ defaultMessage: '交易已替换' }),
        [Reason.cancelled]: intl.formatMessage({
          defaultMessage: '交易已取消',
        }),
        [Reason.repriced]: intl.formatMessage({
          defaultMessage: '交易已调整Gas费',
        }),
      };
      const reasonText = reasonTextMap[err.reason as Reason];
      if (err.cancelled) {
        toast({
          title:
            intl.formatMessage({
              defaultMessage: `交易已取消 `,
            }) + err.reason,
          description: err.hash || '',
        });
      } else if (receipt) {
        toast({
          title:
            reasonText ||
            intl.formatMessage({ defaultMessage: '用户替换了交易' }),
          description: err.reason || '',
        });
        return handleTxResponse(intl, replacement, toast, true);
      } else {
        toast({
          title: intl.formatMessage({ defaultMessage: '替换交易出现异常' }),
          description: err.reason || err.message,
          status: 'error',
        });
      }
    } else {
      toast({
        title: intl.formatMessage({ defaultMessage: '等待交易确认出现错误' }),
        description: ethErrorCodeMap[err.code] || err.reason || err.message,
        status: 'error',
      });
    }
  }
}
